import * as React from "react";
import { Modal, Steps, Form, Select, DatePicker, Button, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DateStrategy } from "app/shared/model/EditProductOffering";
import { validateWorkingWeeksDateStrategy } from "../domain/dateStrategy/workingWeeksStartDate";
import {
  DateStrategyAssign,
  EditPricelistResponse
} from "app/shared/model/EditPricelist";
import { PricingYearId, Centre } from "app/shared/model";
import DateHelper, { CompareTypes } from "app/helpers/dateHelper";
import { CurrentUser } from "app/shared/model/CurrentUser";
import DateStrategyModal from "./DateStrategyModal";
import { normIso } from '../domain/currency/normalizeCurrency';
import { useAvailableProducts } from "../hooks/useAvailableProducts";
import { createNewNonFeeOffering } from "../domain/offering/factories";
import { normalizeNewOfferingStrategy } from "../domain/dateStrategy/normalize";
import {
  getAllowedDateStrategyTypeIds,
  resolveStrategyTypeId,
  toStrategyTypeSelectOptions,
} from "../domain/dateStrategy/allowed";
import {
  getPricingYearWindow,
  shiftPickerDateToEarliestAllowed,
  maxIsoDate,
  minIsoDate,
} from "app/pages/PricelistNew/domain/pricingYear/window";
import { toIso } from "../domain/date/iso";

const { Option } = Select;

interface AddProductOfferingModalProps {
  visible: boolean;
  onCancel: () => void;
  onComplete: (offering: EditPricelistResponse) => void;
  selectedProductType: string;
  selectedPricingYear?: number;
  selectedCentreCode?: string;
  centres: Centre[];
  centreSchools: { id: number; name: string }[];
  pricingYears: PricingYearId[];
  draftPricelist?: EditPricelistResponse[];
  user: CurrentUser;
  enabledCurrencies: string[];
}

type Step = 1 | 2 | 3;

interface Step1Data {
  productId?: number;
  productName?: string;
  fromDate?: Dayjs;
  toDate?: Dayjs;
  productMeta?: any;
}

interface Step2Data {
  strategyTypeId?: number;
  applyFrom?: Date;
}

const hasPendingStrategyData = (strategy?: DateStrategyAssign): boolean => {
  if (!strategy) return false;

  const t = strategy.strategyTypeId;

  if (DateStrategy[t] === "Offering Dates") return true;
  if (DateStrategy[t] === "Recurrence") {
    return Boolean(strategy.recurranceStartDays?.length);
  }
  if (DateStrategy[t] === "Fixed Duration") {
    return Boolean(strategy.fixedDurationDates?.filter((d) => !d.isDeleted).length);
  }
  if (DateStrategy[t] === "Specific Start Dates") {
    return Boolean(strategy.specificStartDates?.filter((d) => !d.isDeleted).length);
  }

  return false;
};

const cloneStrategy = (strategy: DateStrategyAssign): DateStrategyAssign => ({
  ...strategy,
  recurranceStartDays: strategy.recurranceStartDays ? [...strategy.recurranceStartDays] : [],
  specificStartDates: strategy.specificStartDates
      ? strategy.specificStartDates.map((d) => ({ ...d }))
      : [],
  fixedDurationDates: strategy.fixedDurationDates
      ? strategy.fixedDurationDates.map((d) => ({ ...d }))
      : [],
});

const clampStrategyToWindow = (
    strategy: DateStrategyAssign,
    minDate: string,
    maxDate: string
): DateStrategyAssign => {
  const next = cloneStrategy(strategy);

  const currentFrom = toIso(next.fromDate) || minDate;
  const clampedFrom = maxIsoDate(currentFrom, minDate);
  next.fromDate = clampedFrom as any;

  const currentTo = toIso(next.toDate);
  if (currentTo) {
    const clampedTo = minIsoDate(currentTo, maxDate);
    next.toDate = (
        DateHelper.CompareDates(clampedTo, clampedFrom, CompareTypes.SmallerThan)
            ? clampedFrom
            : clampedTo
    ) as any;
  } else {
    next.toDate = null as any;
  }

  if (next.lastStartDate) {
    const lastStart = toIso(next.lastStartDate) || clampedFrom;
    next.lastStartDate = minIsoDate(
        maxIsoDate(lastStart, clampedFrom),
        maxDate
    ) as any;
  }

  next.specificStartDates = (next.specificStartDates ?? []).filter((d) => {
    if (d.isDeleted) return true;
    const iso = toIso(d.date);
    return Boolean(iso && iso >= clampedFrom && iso <= maxDate);
  });

  next.fixedDurationDates = (next.fixedDurationDates ?? []).flatMap((d) => {
    if (d.isDeleted) return [d];

    const start = maxIsoDate(toIso(d.startDate) || clampedFrom, clampedFrom);
    const end = minIsoDate(toIso(d.endDate) || maxDate, maxDate);

    if (DateHelper.CompareDates(end, start, CompareTypes.SmallerThan)) {
      return [];
    }

    return [
      {
        ...d,
        startDate: start as any,
        endDate: end as any,
      },
    ];
  });

  return next;
};

const AddProductOfferingModal: React.FC<AddProductOfferingModalProps> = (props) => {
  const [currentStep, setCurrentStep] = React.useState<Step>(1);
  const [form] = Form.useForm();

  const [step1Data, setStep1Data] = React.useState<Step1Data>({});
  const [step2Data, setStep2Data] = React.useState<Step2Data>({});

  const [dateStrategy, setDateStrategy] = React.useState<DateStrategyAssign | undefined>(undefined);
  const [pendingChanges, setPendingChanges] = React.useState(false);
  const lastStep2StrategyTypeRef = React.useRef<number | undefined>(undefined);

  const selectedPricingYearObj = React.useMemo(() => {
    const list = props.pricingYears ?? [];
    return list.find((py) => py.id === props.selectedPricingYear) ?? list[0];
  }, [props.pricingYears, props.selectedPricingYear]);

  const { allowedStartDate, allowedEndDate, allowedStart, allowedEnd } =
      React.useMemo(
          () => getPricingYearWindow(selectedPricingYearObj),
          [selectedPricingYearObj]
      );
  const centre = React.useMemo(
      () => props.centres.find((c) => c.code === props.selectedCentreCode),
      [props.centres, props.selectedCentreCode]
  );
  const centreName = centre?.name || "";

  const { availableProducts, loadingProducts, loadError } = useAvailableProducts({
    visible: props.visible,
    selectedProductType: props.selectedProductType,
    selectedCentreCode: props.selectedCentreCode,
    centres: props.centres,
    draftPricelist: props.draftPricelist,
    pricingYearAllowedStart: allowedStartDate,
    pricingYearAllowedEnd: allowedEndDate,
  });

  const lastLoadErrorRef = React.useRef<string | null>(null);

  const strategyAllowedStartDate = React.useMemo(() => {
    if (!step1Data.fromDate) return allowedStartDate;

    if (step2Data.applyFrom) {
      return dayjs(step2Data.applyFrom).format("YYYY-MM-DD");
    }

    return step1Data.fromDate.format("YYYY-MM-DD");
  }, [step1Data.fromDate, step2Data.applyFrom, allowedStartDate]);

  const strategyAllowedEndDate = React.useMemo(() => {
    if (!step1Data.toDate) return allowedEndDate;
    return step1Data.toDate.format("YYYY-MM-DD");
  }, [step1Data.toDate, allowedEndDate]);

  React.useEffect(() => {
    if (!props.visible) {
      lastLoadErrorRef.current = null;
      return;
    }

    if (loadError && loadError !== lastLoadErrorRef.current) {
      lastLoadErrorRef.current = loadError;
      message.error("Failed to load products");
    }
  }, [props.visible, loadError]);

  React.useEffect(() => {
    if (!props.visible) {
      setCurrentStep(1);
      setStep1Data({});
      setStep2Data({});
      setDateStrategy(undefined);
      setPendingChanges(false);
      form.resetFields();
    }
  }, [props.visible, form]);

  React.useEffect(() => {
    if (currentStep !== 3) {
      lastStep2StrategyTypeRef.current = undefined;
      return;
    }
    if (!step2Data.strategyTypeId || !step1Data.fromDate || !step1Data.toDate) return;

    const strategyTypeId = step2Data.strategyTypeId;
    const step2TypeChanged = lastStep2StrategyTypeRef.current !== strategyTypeId;
    lastStep2StrategyTypeRef.current = strategyTypeId;

    const nextFromIso = step2Data.applyFrom
        ? dayjs(step2Data.applyFrom).format("YYYY-MM-DD")
        : step1Data.fromDate.format("YYYY-MM-DD");
    const nextToIso = step1Data.toDate.format("YYYY-MM-DD");

    setDateStrategy((prev) => {
      if (prev && !step2TypeChanged) {
        const base = cloneStrategy(prev);
        base.fromDate = nextFromIso as any;

        if (DateStrategy[base.strategyTypeId] === "Offering Dates") {
          base.toDate = nextToIso as any;
        } else if (base.toDate) {
          const currentTo = toIso(base.toDate) || nextToIso;
          base.toDate = minIsoDate(currentTo, nextToIso) as any;
        }

        const clamped = clampStrategyToWindow(base, nextFromIso, nextToIso);
        setPendingChanges(hasPendingStrategyData(clamped));
        return clamped;
      }

      const base = {
        originalFromDate: nextFromIso as any,
        strategyTypeId,
        fromDate: nextFromIso as any,
        toDate:
            DateStrategy[strategyTypeId] === "Offering Dates"
                ? (nextToIso as any)
                : null,
        recurranceStartDays: [],
        specificStartDates: [],
        fixedDurationDates: [],
        isNew: true,
        isArchived: false,
      };

      const clamped = clampStrategyToWindow(base, nextFromIso, nextToIso);
      setPendingChanges(hasPendingStrategyData(clamped));
      return clamped;
    });
  }, [
    currentStep,
    step2Data.strategyTypeId,
    step2Data.applyFrom,
    step1Data.fromDate,
    step1Data.toDate,
  ]);

  const handleStep1Next = async () => {
    try {
      const values = await form.validateFields(["productId", "fromDate", "toDate"]);

      const selectedProduct = availableProducts.find((p) => p.id === values.productId);
      if (!selectedProduct) return message.error("Please select a valid product");

      const fromDate = values.fromDate as Dayjs;
      const toDate = values.toDate as Dayjs;

      if (fromDate.isBefore(allowedStart, "day") || fromDate.isAfter(allowedEnd, "day")) {
        return message.error(
            `From date must be within ${allowedStart.format("DD-MMM-YYYY")} and ${allowedEnd.format("DD-MMM-YYYY")}`
        );
      }

      if (toDate.isBefore(allowedStart, "day") || toDate.isAfter(allowedEnd, "day")) {
        return message.error(
            `To date must be within ${allowedStart.format("DD-MMM-YYYY")} and ${allowedEnd.format("DD-MMM-YYYY")}`
        );
      }

      if (toDate.isBefore(fromDate)) {
        return message.error("To date must be after From date");
      }

      const allowedIds = getAllowedDateStrategyTypeIds(
          props.selectedProductType,
          selectedProduct
      );

      setStep1Data({
        productId: values.productId,
        productName: selectedProduct.name,
        fromDate,
        toDate,
        productMeta: selectedProduct,
      });

      form.setFieldsValue({
        strategyApplyFrom: fromDate,
        strategyType: resolveStrategyTypeId(
            form.getFieldValue("strategyType"),
            allowedIds,
            selectedProduct
        ),
      });
      setCurrentStep(2);
    } catch {
    }
  };

  const handleStep2Next = async () => {
    try {
      const values = await form.validateFields(["strategyType", "strategyApplyFrom"]);
      const applyFromDate = values.strategyApplyFrom || step1Data.fromDate;
      if (!applyFromDate) return message.error("Please select a date strategy apply from date");

      const strategyTypeId = resolveStrategyTypeId(
          values.strategyType,
          allowedStrategyIdsStep2,
          step1Data.productMeta
      );
      if (strategyTypeId === undefined) {
        return message.error("Please select a strategy type");
      }

      setStep2Data({
        strategyTypeId,
        applyFrom: applyFromDate.toDate ? applyFromDate.toDate() : applyFromDate,
      });

      setCurrentStep(3);
    } catch {

    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((currentStep - 1) as Step);
  };

  const handleDateStrategyUpdate = React.useCallback((updatedStrategy: DateStrategyAssign) => {
    const next = clampStrategyToWindow(
        {
          ...updatedStrategy,
          recurranceStartDays: updatedStrategy.recurranceStartDays
              ? [...updatedStrategy.recurranceStartDays]
              : [],
          specificStartDates: updatedStrategy.specificStartDates
              ? updatedStrategy.specificStartDates.map((d) => ({ ...d }))
              : [],
          fixedDurationDates: updatedStrategy.fixedDurationDates
              ? updatedStrategy.fixedDurationDates.map((d) => ({ ...d }))
              : [],
        },
        strategyAllowedStartDate,
        strategyAllowedEndDate
    );

    setDateStrategy(next);
    setPendingChanges(hasPendingStrategyData(next));
  }, [strategyAllowedStartDate, strategyAllowedEndDate]);

  const productId = Form.useWatch("productId", form);
  const fromDate = Form.useWatch("fromDate", form);
  const toDate = Form.useWatch("toDate", form);
  const step1Valid = Boolean(productId && fromDate && toDate);

  const strategyType = Form.useWatch("strategyType", form);
  const strategyApplyFrom = Form.useWatch("strategyApplyFrom", form);
  const step2Valid = strategyType !== undefined && strategyApplyFrom !== undefined;

  const step3Valid = React.useMemo(() => hasPendingStrategyData(dateStrategy), [dateStrategy]);

  const canAddOffering = React.useMemo(() => {
    if (!dateStrategy) return false;
    if (DateStrategy[dateStrategy.strategyTypeId] === "Offering Dates") return true;
    return step3Valid && pendingChanges;
  }, [dateStrategy, step3Valid, pendingChanges]);

  const handleStep3Complete = () => {
    if (!dateStrategy || !step1Data.productId || !step1Data.fromDate || !step1Data.toDate) {
      message.warning("Please complete the configuration");
      return;
    }

    const clampedDateStrategy = clampStrategyToWindow(
        dateStrategy,
        strategyAllowedStartDate,
        strategyAllowedEndDate
    );

    const workingWeeksError = validateWorkingWeeksDateStrategy(
      clampedDateStrategy,
      step1Data.productMeta?.unitTypeId,
      step1Data.productMeta?.unitTypeText
    );
    if (workingWeeksError) {
      message.error(workingWeeksError);
      return;
    }

    const currencyIsoCodes = props.enabledCurrencies.length
        ? props.enabledCurrencies
        : [normIso(centre?.currencyCode ?? "CAD")].filter(Boolean);

    const normalizedDateStrategy = normalizeNewOfferingStrategy(clampedDateStrategy);
    const schoolIds = props.centreSchools.map((s) => ({ id: s.id }));

    const result = createNewNonFeeOffering({
      productTypeName: props.selectedProductType,
      productId: step1Data.productId,
      name: step1Data.productName || "",
      schoolIds,
      currencyIsoCodes,
      periodStartDate: step1Data.fromDate.format("YYYY-MM-DD"),
      periodEndDate: step1Data.toDate.format("YYYY-MM-DD"),
      dateStrategyAssignments: [normalizedDateStrategy],
    });

    if (!result.ok) {
      message.error(result.error);
      return;
    }

    if (step1Data.productMeta) {
      Object.assign(result.offering as any, {
        unitTypeId: step1Data.productMeta.unitTypeId,
        unitTypeName: step1Data.productMeta.unitTypeText,
        unitTypeText: step1Data.productMeta.unitTypeText,
      });
    }

    props.onComplete(result.offering);
  };

  const modalTitle = `Add new ${props.selectedProductType} offering${centreName ? ` in ${centreName}` : ""}`;

  const allowedStrategyIdsStep2 = React.useMemo(
      () => getAllowedDateStrategyTypeIds(props.selectedProductType, step1Data.productMeta),
      [props.selectedProductType, step1Data.productMeta]
  );

  const strategyTypeOptions = React.useMemo(
      () => toStrategyTypeSelectOptions(allowedStrategyIdsStep2),
      [allowedStrategyIdsStep2]
  );

  React.useEffect(() => {
    if (!props.visible) return;

    const current = form.getFieldValue("strategyType");
    const resolved = resolveStrategyTypeId(
        current,
        allowedStrategyIdsStep2,
        step1Data.productMeta
    );

    if (resolved !== undefined && resolved !== current) {
      form.setFieldsValue({ strategyType: resolved });
    }
  }, [
    props.visible,
    props.selectedProductType,
    allowedStrategyIdsStep2,
    step1Data.productMeta,
    form,
  ]);

  return (
      <Modal
          title={modalTitle}
          open={props.visible}
          onCancel={props.onCancel}
          footer={null}
          width={800}
          destroyOnHidden
      >
        <Steps
            current={currentStep - 1}
            items={[
              { title: "Product Selection" },
              { title: "Choose Date Strategy" },
              { title: "Setup Date Strategy" },
            ]}
            style={{ marginBottom: 24 }}
        />

        <Form form={form} layout="vertical">
          {currentStep === 1 && (
              <div>
                <Form.Item label="Product to add" name="productId" rules={[{ required: true, message: "Please select a product" }]}>
                  <Select
                      placeholder="Select a product"
                      loading={loadingProducts}
                      showSearch
                      filterOption={(input, option) =>
                          String((option as any)?.children ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                      }
                  >
                    {availableProducts.map((product) => (
                        <Option key={product.id} value={product.id}>
                          {product.name}
                        </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Pricing to apply from" name="fromDate" rules={[{ required: true, message: "Please select a from date" }]}>
                  <DatePicker
                      style={{ width: "100%" }}
                      format={DateHelper.DATE_PICKER_FORMATS}
                      placeholder="dd-mmm-yyyy"
                      defaultPickerValue={allowedStart}
                      disabledDate={(date) =>
                          date.isBefore(allowedStart, "day") || date.isAfter(allowedEnd, "day")
                      }
                      onChange={(value) => {
                        if (!value) return;

                        const normalizedIso = shiftPickerDateToEarliestAllowed(
                            value,
                            allowedStartDate,
                            allowedEndDate
                        );

                        if (normalizedIso !== value.format("YYYY-MM-DD")) {
                          form.setFieldValue("fromDate", dayjs(normalizedIso));

                          const currentTo = form.getFieldValue("toDate") as Dayjs | undefined;
                          if (currentTo && dayjs(normalizedIso).isAfter(currentTo, "day")) {
                            form.setFieldValue("toDate", dayjs(normalizedIso));
                          }
                        }
                      }}
                  />
                </Form.Item>

                <Form.Item
                    label="Pricing to apply to"
                    name="toDate"
                    rules={[
                      { required: true, message: "Please select a to date" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const f = getFieldValue("fromDate");
                          if (!value || !f) return Promise.resolve();
                          if (value.isBefore(f)) return Promise.reject(new Error("To date must be after or equal to From date"));
                          return Promise.resolve();
                        },
                      }),
                    ]}
                >
                  <DatePicker
                      style={{ width: "100%" }}
                      format={DateHelper.DATE_PICKER_FORMATS}
                      placeholder="dd-mmm-yyyy"
                      defaultPickerValue={allowedStart}
                      disabledDate={(date) => {
                        const f = form.getFieldValue("fromDate") as Dayjs | undefined;
                        const minTo = f ?? allowedStart;

                        return date.isBefore(minTo, "day") || date.isAfter(allowedEnd, "day");
                      }}
                      onChange={(value) => {
                        if (!value) return;

                        const f = form.getFieldValue("fromDate") as Dayjs | undefined;
                        const minToIso = (f ?? allowedStart).format("YYYY-MM-DD");

                        const normalizedIso = shiftPickerDateToEarliestAllowed(
                            value,
                            minToIso,
                            allowedEndDate
                        );

                        if (normalizedIso !== value.format("YYYY-MM-DD")) {
                          form.setFieldValue("toDate", dayjs(normalizedIso));
                        }
                      }}
                  />
                </Form.Item>

                <div style={{ textAlign: "right", marginTop: 24 }}>
                  <Button type="primary" onClick={handleStep1Next} disabled={!step1Valid}>
                    Next <ArrowRightOutlined />
                  </Button>
                </div>
              </div>
          )}

          {currentStep === 2 && (
              <div>
                <Form.Item label="Strategy Type" name="strategyType" rules={[{ required: true, message: "Please select a strategy type" }]}>
                  <Select placeholder="- strategy type -" options={strategyTypeOptions} />
                </Form.Item>

                {step1Data.fromDate && step1Data.toDate && (
                    <Form.Item
                        label="Date Strategy to apply from"
                        name="strategyApplyFrom"
                        rules={[{ required: true, message: "Please select a date" }]}
                        initialValue={step1Data.fromDate}
                    >
                      <DatePicker
                          style={{ width: "100%" }}
                          format={DateHelper.DATE_PICKER_FORMATS}
                          placeholder="dd-mmm-yyyy"
                          disabledDate={(date) =>
                              date.isBefore(step1Data.fromDate!, "day") ||
                              date.isAfter(step1Data.toDate!, "day")
                          }
                          onChange={(value) => {
                            if (!value || !step1Data.fromDate || !step1Data.toDate) return;

                            const normalizedIso = shiftPickerDateToEarliestAllowed(
                                value,
                                step1Data.fromDate.format("YYYY-MM-DD"),
                                step1Data.toDate.format("YYYY-MM-DD")
                            );

                            if (normalizedIso !== value.format("YYYY-MM-DD")) {
                              form.setFieldValue("strategyApplyFrom", dayjs(normalizedIso));
                            }
                          }}
                      />
                    </Form.Item>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="primary" onClick={handleStep2Next} disabled={!step2Valid}>
                    Next <ArrowRightOutlined />
                  </Button>
                </div>
              </div>
          )}

          {currentStep === 3 && dateStrategy && step1Data.fromDate && step1Data.toDate && (
              <div>
                {DateStrategy[dateStrategy.strategyTypeId] === "Offering Dates" ? (
                    <div style={{ padding: "20px 0" }}>
                      <p>
                        Offering Dates strategy is configured. The offering will be available from{" "}
                        <strong>{dayjs(strategyAllowedStartDate).format("DD-MMM-YYYY")}</strong> to{" "}
                        <strong>{dayjs(strategyAllowedEndDate).format("DD-MMM-YYYY")}</strong>.
                      </p>
                    </div>
                ) : (
                    <DateStrategyModal
                        dateStrategyToUpdateCreate={dateStrategy}
                        selectedProduct={{
                          isNew: true,
                          type: props.selectedProductType,
                          name: step1Data.productName ?? "",
                          unitTypeId: step1Data.productMeta?.unitTypeId,
                          unitTypeName: step1Data.productMeta?.unitTypeText,
                        } as EditPricelistResponse}
                        isUnsavedOffering={true}
                        createNew={true}
                        minDateFrom={step1Data.fromDate.format("YYYY-MM-DD")}
                        maxDateFrom={step1Data.toDate.format("YYYY-MM-DD")}
                        allowedStartDate={step1Data.fromDate.format("YYYY-MM-DD")}
                        allowedEndDate={step1Data.toDate.format("YYYY-MM-DD")}
                        onPendingChanges={setPendingChanges}
                        onDateStrategyUpdate={handleDateStrategyUpdate}
                    />
                )}

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="primary" onClick={handleStep3Complete} disabled={!canAddOffering}>
                    Add Offering
                  </Button>
                </div>
              </div>
          )}
        </Form>
      </Modal>
  );
};

export default AddProductOfferingModal;